import { useState } from 'react';

/**
 * ValidationResultsPanel - Display validation results with pass/fail summary
 *
 * Features:
 * - Pass/fail summary with visual indicators
 * - Detailed error listing with row/column references
 * - Download validation errors as CSV
 * - Block submission if validation fails
 */
function ValidationResultsPanel({ validationResult, onDownloadErrors, allowProceed = true }) {
  const [showAllErrors, setShowAllErrors] = useState(false);

  if (!validationResult) {
    return null;
  }

  const { valid, error_count, warning_count, errors, summary } = validationResult;
  const displayErrors = showAllErrors ? errors : errors?.slice(0, 10) || [];
  const hasMoreErrors = errors && errors.length > 10;

  // Generate CSV content for errors
  const generateErrorsCSV = () => {
    if (!errors || errors.length === 0) return '';

    const headers = ['Row Number', 'Column Name', 'Field Name', 'Error Message', 'Value'];
    const rows = errors.map(err => [
      err.row_number,
      err.column_name || '',
      err.field_name || '',
      err.error_message || '',
      err.value || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        let val = String(cell).replace(/"/g, '""');
        // Prevent CSV formula injection — prefix dangerous leading characters
        if (/^[=+@\-]/.test(val)) {
          val = "'" + val;
        }
        return `"${val}"`;
      }).join(','))
    ].join('\n');

    return csvContent;
  };

  const handleDownloadErrors = () => {
    const csvContent = generateErrorsCSV();
    if (!csvContent) return;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `validation-errors-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    if (onDownloadErrors) {
      onDownloadErrors();
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header with pass/fail indicator */}
      <div className={`p-4 ${valid ? 'bg-green-50 border-b border-green-200' : 'bg-red-50 border-b border-red-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {valid ? (
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
            <div>
              <h3 className={`text-lg font-semibold ${valid ? 'text-green-900' : 'text-red-900'}`}>
                {valid ? 'Validation Passed' : 'Validation Failed'}
              </h3>
              <p className={`text-sm ${valid ? 'text-green-700' : 'text-red-700'}`}>
                {summary}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-4">
            {error_count > 0 && (
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{error_count}</div>
                <div className="text-xs text-red-600">Errors</div>
              </div>
            )}
            {warning_count > 0 && (
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{warning_count}</div>
                <div className="text-xs text-yellow-600">Warnings</div>
              </div>
            )}
            {valid && (
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">✓</div>
                <div className="text-xs text-green-600">All Clear</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error details */}
      {!valid && errors && errors.length > 0 && (
        <div className="bg-white">
          {/* Download button */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <span className="text-sm text-gray-600 font-medium">Validation Errors</span>
            <button
              onClick={handleDownloadErrors}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Errors CSV
            </button>
          </div>

          {/* Error table */}
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Row
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Column
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Field
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Error
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayErrors.map((error, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {error.row_number}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 font-mono">
                      {error.column_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {error.field_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600">
                      {error.error_message}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono truncate max-w-xs" title={error.value}>
                      {error.value || <span className="text-gray-400 italic">(empty)</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Show more/less button */}
          {hasMoreErrors && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-center">
              <button
                onClick={() => setShowAllErrors(!showAllErrors)}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {showAllErrors ? (
                  <>Show Less</>
                ) : (
                  <>Show All {errors.length} Errors</>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Warning if validation failed and trying to proceed */}
      {!valid && !allowProceed && (
        <div className="px-4 py-3 bg-yellow-50 border-t border-yellow-200">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-sm text-yellow-800">
              <strong>Cannot proceed with validation errors.</strong> Please download the errors CSV, fix the issues in your file, and re-upload.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ValidationResultsPanel;
